// Workbench panel — recipe list + queue view for the personal workshop.
//
// Left column: recipe list (workbench-recipe-list.ts, extracted for the 400-line cap).
// Right column: queue display (workbench-queue.ts, extracted for the 400-line cap).
//
// Input suppression: the caller (main.ts) is responsible for calling
// LocalController.setModal(true/false). See DECISIONS "modal input suppression".
//
// open(skills, workshop) renders a snapshot. update(workshop) re-renders the
// queue column in-place; Step 18 calls update() on each workshop server message.

import { Application, Container, Graphics, Text } from "pixi.js";
import { balance } from "./types.ts";
import { buildRecipeList } from "./workbench-recipe-list.ts";
import { WorkbenchQueue } from "./workbench-queue.ts";
import { WorkbenchActionBar } from "./workbench-action-bar.ts";
import {
  buildQueueCraftMessage,
  buildClaimCompletedJobsMessage,
  buildCancelCraftMessage,
} from "../../shared/workshop-messages.ts";
import type { ClientMessage, InventorySlot } from "../../shared/messages.ts";
import { getRecipe as getRecipeById, type Recipe } from "../../shared/recipes.ts";
import type { SkillEntry, WorkshopState } from "./types.ts";
import { assessMaterials, type MaterialsResult } from "./workbench-materials.ts";
import {
  createInfoPane,
  renderEmpty as renderInfoEmpty,
  renderRecipe as renderInfoRecipe,
  type InfoPaneHandle,
} from "./workbench-info-pane.ts";

// -----------------------------------------------------------------------
// Layout constants
// -----------------------------------------------------------------------

const PAD = 12; // contracts:allow-literal — inner padding px
const HEADER_H = 44; // contracts:allow-literal — title bar height px
const FOOTER_HINT_H = 24; // contracts:allow-literal — footer hint height px
const INFO_PANE_H = 100; // contracts:allow-literal — detail pane height px

const ACTION_BAR_H = 48; // contracts:allow-literal — action bar height px

const PANEL_BG = 0x12122a;
const PANEL_BORDER = 0x4455cc;
const PANEL_BORDER_W = 2; // contracts:allow-literal
const DIVIDER_COLOR = 0x3344aa;
const TITLE_COLOR = 0xeeeeff;
const MUTED_COLOR = 0x666688;
const TITLE_SIZE = 18; // contracts:allow-literal
const BODY_SIZE = 11; // contracts:allow-literal

// -----------------------------------------------------------------------
// WorkbenchUI
// -----------------------------------------------------------------------

export class WorkbenchUI {
  readonly root: Container;
  private readonly app: Application;
  private readonly queue: WorkbenchQueue;
  private readonly actionBar: WorkbenchActionBar;

  // Set by main.ts after NetworkClient is constructed.
  sendFn: (msg: ClientMessage) => void = () => {};

  private _isOpen = false;
  private _skillXpMap: Record<string, number> = {};
  private _workshop: WorkshopState = { jobs: [], completedUnclaimed: [] };
  // Phase 7 Block D — inventory provider used by the action bar's
  // material gate and the info pane's per-input have/need rows.
  private _inventoryProvider: () => readonly InventorySlot[] = () => [];

  // Currently selected recipe (drives action bar).
  private _selectedRecipe: Recipe | null = null;
  private _selectedUnlocked = false;

  // Geometry cached from last rebuild() — needed by update() to re-position queue column
  private _queueGeom: { parent: Container; x: number; y: number; w: number; h: number } | null = null;

  // Info pane handle — rebuilt on each rebuild + on info-pane updates.
  private info: InfoPaneHandle | null = null;

  constructor(app: Application) {
    this.app = app;
    this.root = new Container();
    this.root.name = "workbench-panel";
    this.root.visible = false;
    this.root.zIndex = 200; // contracts:allow-literal — above inventory (100)
    app.stage.sortableChildren = true;
    app.stage.addChild(this.root);
    this.queue = new WorkbenchQueue(app);
    this.actionBar = new WorkbenchActionBar();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  setInventoryProvider(p: () => readonly InventorySlot[]): void {
    this._inventoryProvider = p;
    this.actionBar.setInventoryProvider(p);
  }

  open(skills: SkillEntry[], workshop: WorkshopState): void {
    this._skillXpMap = Object.fromEntries(skills.map((s) => [s.skillId, s.xp]));
    this._workshop = workshop;
    this.rebuild();
    this.root.visible = true;
    this._isOpen = true;
  }

  close(): void {
    this.root.visible = false;
    this._isOpen = false;
    this.info = null;
    this._queueGeom = null;
    this._selectedRecipe = null;
    this._selectedUnlocked = false;
    this.queue.destroy();
    this.actionBar.destroy();
    this.root.removeChildren();
  }

  // Called by Step 18 message handlers whenever workshop state changes.
  update(workshop: WorkshopState): void {
    this._workshop = workshop;
    if (!this._isOpen || !this._queueGeom) return;
    const { parent, x, y, w, h } = this._queueGeom;
    this.queue.update(parent, x, y, w, h, workshop);
  }

  // Phase 7 Block D — inventoryUpdated server message arms this so the
  // material gate + info-pane have/need rows refresh without reopening.
  refreshOnInventoryChange(): void {
    if (!this._isOpen) return;
    this.actionBar.refresh();
    this.repaintInfoPane();
  }

  // Test surface: returns the live material assessment for the selected
  // recipe + current batch count, mirroring what the action bar gates on
  // and what the info pane renders. Null when no recipe is selected.
  currentMaterials(): MaterialsResult | null {
    return this.actionBar.currentMaterials();
  }

  // The currently-selected recipe id, or null. Browser harness reads
  // this via __nexusDebug.state() to assert recipe-selection plumbing.
  get selectedRecipeId(): string | null {
    return this._selectedRecipe?.id ?? null;
  }

  // Phase 7 Block D — programmatic recipe selection for the browser
  // harness. Mirrors the production click path: validates the recipe
  // exists, computes the unlocked status against current skills, and
  // routes through the same setRecipe path the recipe-list pointer-tap
  // handler uses. Required because PixiJS pointer events on the
  // recipe-list rows are unreliable in headless Chromium (same DECISION
  // class as the market action hooks).
  selectRecipeForTest(recipeId: string): { ok: boolean; reason?: string } {
    const recipe = this._workshop ? this.findRecipeForTest(recipeId) : null;
    if (!recipe) return { ok: false, reason: "unknown_recipe" };
    const unlocked = recipe.skillGates.every((g) => (this._skillXpMap[g.skillId] ?? 0) >= g.minXp);
    this._selectedRecipe = recipe;
    this._selectedUnlocked = unlocked;
    this.actionBar.setRecipe(recipe);
    if (this._isOpen) this.repaintInfoPane();
    return { ok: true };
  }

  private findRecipeForTest(recipeId: string): Recipe | null {
    return getRecipeById(recipeId) ?? null;
  }

  // -----------------------------------------------------------------------
  // Panel construction
  // -----------------------------------------------------------------------

  private rebuild(): void {
    this.root.removeChildren();

    const sw = this.app.renderer.width;
    const sh = this.app.renderer.height;
    const pw = Math.round(sw * balance.workbench.panelWidthRatio);
    const ph = Math.round(sh * balance.workbench.panelHeightRatio);
    const px = Math.round((sw - pw) / 2); // contracts:allow-literal — centering idiom
    const py = Math.round((sh - ph) / 2); // contracts:allow-literal — centering idiom

    const panel = new Container();
    panel.x = px;
    panel.y = py;
    this.root.addChild(panel);

    // Click-blocker backdrop
    const backdrop = new Graphics().rect(0, 0, sw, sh).fill({ color: 0x000000, alpha: 0.5 }); // contracts:allow-literal
    backdrop.x = -px;
    backdrop.y = -py;
    panel.addChild(backdrop);

    // Panel background
    panel.addChild(
      new Graphics()
        .roundRect(0, 0, pw, ph, 8) // contracts:allow-literal — corner radius
        .fill({ color: PANEL_BG })
        .stroke({ color: PANEL_BORDER, width: PANEL_BORDER_W }),
    );

    // Title + ESC hint
    const title = new Text({
      text: "Workshop",
      style: { fontSize: TITLE_SIZE, fill: TITLE_COLOR, fontFamily: "monospace", fontWeight: "bold" },
    });
    title.x = PAD;
    title.y = Math.round((HEADER_H - title.height) / 2); // contracts:allow-literal
    panel.addChild(title);

    const escHint = new Text({
      text: "[ESC] Close",
      style: { fontSize: BODY_SIZE, fill: MUTED_COLOR, fontFamily: "monospace" },
    });
    escHint.x = pw - PAD - escHint.width;
    escHint.y = Math.round((HEADER_H - escHint.height) / 2); // contracts:allow-literal
    panel.addChild(escHint);

    // Horizontal divider under title
    panel.addChild(new Graphics().rect(0, HEADER_H, pw, 1).fill({ color: DIVIDER_COLOR })); // contracts:allow-literal

    // Column layout
    const leftW = Math.round(pw * balance.workbench.leftColRatio);

    // Vertical divider
    panel.addChild(
      new Graphics()
        .rect(leftW, HEADER_H + 1, 1, ph - HEADER_H - 1) // contracts:allow-literal
        .fill({ color: DIVIDER_COLOR }),
    );

    // Right column — queue display
    const rightX = leftW + 1; // contracts:allow-literal — 1px for divider
    const rightW = pw - rightX;
    const rightY = HEADER_H + 1; // contracts:allow-literal — 1px for header divider
    const rightH = ph - rightY;
    this._queueGeom = { parent: panel, x: rightX, y: rightY, w: rightW, h: rightH };
    this.queue.build(panel, rightX, rightY, rightW, rightH, this._workshop);

    // Left column content — layout from top, then fixed blocks at bottom
    const contentTop = HEADER_H + 1;
    const infoPaneY = ph - INFO_PANE_H - ACTION_BAR_H - FOOTER_HINT_H;
    const actionBarY = ph - ACTION_BAR_H - FOOTER_HINT_H;
    const contentH = infoPaneY - contentTop - PAD;

    buildRecipeList(
      panel,
      0,
      contentTop,
      leftW,
      this._skillXpMap,
      (recipe, unlocked) => {
        // Hover: show hovered recipe; on leave, fall back to selection or clear.
        if (recipe) this.updateInfoPane(recipe, unlocked);
        else if (this._selectedRecipe) this.updateInfoPane(this._selectedRecipe, this._selectedUnlocked);
        else this.clearInfoPane();
      },
      (recipe, unlocked) => {
        this._selectedRecipe = recipe;
        this._selectedUnlocked = unlocked;
        this.actionBar.setRecipe(recipe);
        // Update info pane immediately on select/deselect so it doesn't flicker to blank.
        if (recipe) this.updateInfoPane(recipe, unlocked);
        else this.clearInfoPane();
      },
    );

    void contentH; // reserved for future scroll implementation

    // Info pane (extracted to workbench-info-pane.ts).
    this.info = createInfoPane(panel, 0, infoPaneY, leftW, INFO_PANE_H);

    // Action bar — wired to send queue craft messages via sendFn
    this.actionBar.onQueueCraft = (recipeId, batchCount) => {
      this.sendFn(buildQueueCraftMessage(recipeId, batchCount));
    };
    // Phase 7 Block D — info pane re-renders when batch count changes so
    // the per-input have/need rows reflect the new total.
    this.actionBar.onBatchCountChange = () => this.repaintInfoPane();
    this.actionBar.setInventoryProvider(this._inventoryProvider);
    this.actionBar.build(panel, 0, actionBarY, leftW, ACTION_BAR_H);

    // Per-row claim (Phase 7 Block D): each completed-unclaimed row
    // sends claimCompletedJobs scoped to that jobId so the player can
    // pick winners individually rather than draining the whole tray.
    this.queue.onClaimRequest = (jobId) => {
      this.sendFn(buildClaimCompletedJobsMessage(jobId));
    };
    // Cancel-in-progress (Phase 7 Block D): per-row cancel on queued
    // and running jobs. Server-side cancelCraft already supports both
    // statuses with full refund.
    this.queue.onCancelRequest = (jobId) => {
      this.sendFn(buildCancelCraftMessage(jobId));
    };

    // Footer hint
    const footerText = new Text({
      text: "Click a recipe to select  •  [B] or [ESC] to close",
      style: { fontSize: BODY_SIZE, fill: MUTED_COLOR, fontFamily: "monospace" },
    });
    footerText.x = PAD;
    footerText.y = ph - FOOTER_HINT_H + Math.round((FOOTER_HINT_H - footerText.height) / 2); // contracts:allow-literal
    panel.addChild(footerText);
  }

  private repaintInfoPane(): void {
    if (!this.info) return;
    if (this._selectedRecipe) this.updateInfoPane(this._selectedRecipe, this._selectedUnlocked);
    else this.clearInfoPane();
  }

  private updateInfoPane(recipe: Recipe, unlocked: boolean): void {
    if (!this.info) return;
    const batchCount = this.actionBar.batchCount;
    const materials = assessMaterials(recipe, this._inventoryProvider(), batchCount);
    renderInfoRecipe(this.info, recipe, unlocked, materials, batchCount, this._skillXpMap);
  }

  private clearInfoPane(): void {
    if (!this.info) return;
    renderInfoEmpty(this.info);
  }
}
